import { Dialog, DialogContent } from "@cmp-src/primitives/Dialog";
import { useAuthenticated } from "@gettactic/hooks";
import { MapProvider } from "@/features/map/MapProvider";
import { MapLoadingSpinner } from "@/features/map/components/MapView/MapLoadingSpinner";
import { MapPageContent } from "@/features/map/components/Page/MapPageContent";
import { useMapSelectedOfficeUrlSync } from "@/features/map/components/Page/useMapSelectedOfficeUrlSync";
import { useLayout } from "@/providers/LayoutProvider/context";
import { ReserveProvider } from "@/features/reserve/providers/ReserveProvider";
import { useRouter } from "@/lib/useRouter";
import React from "react";
import { MapModalParams } from "./types";

interface MapReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  params: MapModalParams;
  onSuccess: () => void;
}

const classes = {
  NavigationButtons:
    "h-8 w-8 rounded-full bg-background/90 backdrop-blur-sm hover:bg-background text-neutral-500",
  NavigationButtonsIcon: "h-4 w-4"
};

export const MapReservationModal: React.FC<MapReservationModalProps> = ({
  isOpen,
  onClose,
  params,
  onSuccess
}) => {
  const {
    userContext: { currentOffice, authenticatedUser },
    setUserContext
  } = useAuthenticated();
  const { selectedFloor } = useLayout();
  const router = useRouter();

  const { isLoaded, initialFloorId } = useMapSelectedOfficeUrlSync(false);

  // If the modal is opened with a specific officeId, temporarily switch the global office context
  // so all map UI (filters, availability, etc.) is consistent. Restore it on close.
  const previousOfficeIdRef = React.useRef<string | null>(null);
  const didOverrideOfficeRef = React.useRef(false);
  const appliedDefaultOfficeIdRef = React.useRef<string | "none" | null>(null);

  // Apply the modal's default office ONCE when opening.
  // After that, user can freely switch offices without interference.
  React.useEffect(() => {
    if (!isOpen) return;

    // Only run the initialization logic once per modal open
    if (appliedDefaultOfficeIdRef.current !== null) return;

    const targetOfficeId = params.officeId ? String(params.officeId) : null;
    if (!targetOfficeId) {
      appliedDefaultOfficeIdRef.current = "none"; // Mark as initialized even if no default office
      return;
    }

    // If we're already on the target office, just mark it as applied
    if (currentOffice?.id === targetOfficeId) {
      appliedDefaultOfficeIdRef.current = targetOfficeId;
      return;
    }

    // Save the current office so we can restore it on close
    previousOfficeIdRef.current = currentOffice?.id ?? null;
    didOverrideOfficeRef.current = true;

    // Switch to the target office
    const officeExists = authenticatedUser?.offices?.byId?.[targetOfficeId];
    if (officeExists) {
      setUserContext({
        type: "updateCurrentOffice",
        payload: targetOfficeId
      });
      appliedDefaultOfficeIdRef.current = targetOfficeId;
    } else {
      // do nothing, applied currentOffice?.id is already the target office
    }
  }, [
    isOpen,
    params.officeId,
    currentOffice?.id,
    authenticatedUser?.offices?.byId,
    setUserContext
  ]);

  React.useEffect(() => {
    if (isOpen) return;

    // Modal closed - restore the previous office and reset all tracking refs
    if (didOverrideOfficeRef.current && previousOfficeIdRef.current) {
      setUserContext({
        type: "updateCurrentOffice",
        payload: previousOfficeIdRef.current
      });
    }

    didOverrideOfficeRef.current = false;
    previousOfficeIdRef.current = null;
    appliedDefaultOfficeIdRef.current = null;
  }, [isOpen, setUserContext]);

  // Calculate start date from params
  const queryDate = router.query.date || router.query.startDate;
  const startDateParam =
    params.date || (queryDate ? String(queryDate) : undefined);

  // Calculate end time from URL
  const endTimeParam = router.query.endTime
    ? String(router.query.endTime)
    : undefined;

  // Get time mode from URL
  const timeModeParam = router.query.timeMode as string;

  // Determine which floor to show
  // Check params.floorId first, then duplicateData.floorId (for duplicate flow),
  // then initialFloorId from URL (handles both query and hash formats), finally fall back to selected floor
  const floorIdToShow =
    params.floorId ||
    params.duplicateData?.floorId ||
    initialFloorId ||
    selectedFloor?.id;

  const sectionFromUrl = router.query.section as string;
  const validSections = ["people", "rooms", "desks", "other"];
  // No fallback: an unspecified section lets the panel open on the
  // organization's first configured tab rather than always Desks.
  const defaultTab =
    sectionFromUrl && validSections.includes(sectionFromUrl)
      ? (sectionFromUrl as "people" | "rooms" | "desks" | "other")
      : params.defaultTab;

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="!border-0 !shadow-none !outline-none !ring-0 h-[90vh] max-w-[93vw] gap-0 border-none p-0 md:border-none md:outline-none"
        // biome-ignore lint/complexity/noUselessFragments: keeps default close button away
        customCloseButton={<></>}
        style={{
          borderWidth: 0
        }}
      >
        {/* Map Content */}
        <div className="h-full w-full">
          {!isLoaded ? (
            <div className="flex h-full w-full items-center justify-center px-6 py-4">
              <MapLoadingSpinner />
            </div>
          ) : (
            <ReserveProvider
              key={params.officeId ?? currentOffice?.id}
              isOnReservationModal={true}
              withModal={false}
            >
              <MapProvider
                key={initialFloorId || floorIdToShow || "map-provider"}
                isEmbedded={false}
                initialFloorId={floorIdToShow}
                startDate={startDateParam || null}
                initialEndDate={endTimeParam || null}
                officeId={currentOffice?.id ?? undefined}
                resourceId={params.resourceId}
                reservationModalIsOpen={true}
                isOnReservationModal={true}
                section={defaultTab}
                initialSearchText={router.query.searchText as string}
                initialTimeMode={timeModeParam as any}
                disableUrlOfficeSync={true}
              >
                <MapPageContent
                  isOnReservationModal={true}
                  isModal={true}
                  onSuccess={onSuccess}
                  onClose={onClose}
                  duplicateData={params.duplicateData}
                  editSchedule={params.editSchedule}
                />
              </MapProvider>
            </ReserveProvider>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
